import { useState, useCallback } from 'react';
import { useServices } from 'services';
import useLoadingStateManager from './useLoadingStateManager';
import { LoadingStatus } from 'types/LoadingStateManager';
import User from 'types/User';
import UserJson from 'types/UserJson';

interface UseUserApprovalReturn {
  pendingUsers: User[];
  recentlyReviewedUsers: User[];
  isLoading: boolean;
  fetchUsers: () => Promise<void>;
  approveUser: (user: User) => Promise<void>;
  denyUser: (user: User) => Promise<void>;
}

export default function useUserApproval(): UseUserApprovalReturn {
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [recentlyReviewedUsers, setRecentlyReviewedUsers] = useState<User[]>([]);
  const { authService } = useServices();
  const loadingStateManager = useLoadingStateManager();

  const isLoading = loadingStateManager.loadingState.status === LoadingStatus.LOADING;

  const fetchUsers = useCallback(async (): Promise<void> => {
    try {
      const [pendingResponse, recentlyApprovedUsersData] = await Promise.all([
        authService.rdmApiService.axios.get('/get-pending-approval-users'),
        authService.fetchRecentlyApprovedUsers(10)
      ]);

      const pendingUsersData = pendingResponse.data?.map((userData: UserJson) =>
        authService.cacheUserIfNeeded(userData)
      ) || [];

      setPendingUsers(pendingUsersData);
      setRecentlyReviewedUsers(recentlyApprovedUsersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [authService]);

  const approveUser = useCallback(async (user: User): Promise<void> => {
    const refreshData = async (): Promise<void> => {
      await authService.approve_new_user(user);
      // Refresh both lists after approval
      const [pendingResponse, updatedRecentlyApprovedUsers] = await Promise.all([
        authService.rdmApiService.axios.get('/get-pending-approval-users'),
        authService.fetchRecentlyApprovedUsers(10)
      ]);

      const updatedPendingUsers = pendingResponse.data?.map((userData: UserJson) =>
        authService.cacheUserIfNeeded(userData)
      ) || [];

      setPendingUsers(updatedPendingUsers);
      setRecentlyReviewedUsers(updatedRecentlyApprovedUsers);
    };

    await loadingStateManager.startLoading({
      waitOn: refreshData(),
    });
  }, [authService, loadingStateManager]);

  const denyUser = useCallback(async (user: User): Promise<void> => {
    const refreshData = async (): Promise<void> => {
      await authService.deny_new_user(user);
      // Refresh both lists after denials
      const [pendingResponse, updatedRecentlyApprovedUsers] = await Promise.all([
        authService.rdmApiService.axios.get('/get-pending-approval-users'),
        authService.fetchRecentlyApprovedUsers(10)
      ]);

      const updatedPendingUsers = pendingResponse.data?.map((userData: UserJson) =>
        authService.cacheUserIfNeeded(userData)
      ) || [];

      setPendingUsers(updatedPendingUsers);
      setRecentlyReviewedUsers(updatedRecentlyApprovedUsers);
    };

    await loadingStateManager.startLoading({
      waitOn: refreshData(),
    });
  }, [authService, loadingStateManager]);

  return {
    pendingUsers,
    recentlyReviewedUsers,
    isLoading,
    fetchUsers,
    approveUser,
    denyUser,
  };
}